import React from 'react';
import type { ImageCardComponent as ImageCardComponentType } from '../schemas/validation';
import { ButtonLink } from './shared/ButtonLink';
import { useThemeSafe } from '../contexts/ThemeContext';
import { useCustomColors, useBrandFonts } from '../contexts/CustomColorsContext';
import { getFontFamily } from '../utils/fontLoader';
import { getThemeStyles } from '../styles/themes';
import { getCroppedImageStyles, hasCropApplied } from './ComponentEditModal/shared/cropUtils';

interface ImageCardComponentProps {
  component: ImageCardComponentType;
}

export const ImageCardComponent: React.FC<ImageCardComponentProps> = ({ component }) => {
  const { data } = component;
  const { currentTheme } = useThemeSafe();
  const customColors = useCustomColors();
  const themeStyles = getThemeStyles(currentTheme, customColors || undefined);

  const brandFonts = useBrandFonts();

  // Font styles for headings and body text
  const headingFontStyle = brandFonts?.headingFont
    ? { fontFamily: getFontFamily(brandFonts.headingFont) }
    : {};
  const bodyFontStyle = brandFonts?.bodyFont
    ? { fontFamily: getFontFamily(brandFonts.bodyFont) }
    : {};

  // Color priority: component.data.customColors > theme colors > fallback
  const backgroundColor =
    data.customColors?.background || themeStyles.imageCard?.background || '#ffffff';
  const overlayColor =
    data.customColors?.overlayColor || themeStyles.imageCard?.overlayColor || '#000000';
  const titleColor = data.customColors?.title || themeStyles.imageCard?.title || '#ffffff';
  const descriptionColor =
    data.customColors?.description || themeStyles.imageCard?.description || '#e5e7eb';
  const buttonBgColor = data.customColors?.buttonBg || themeStyles.imageCard?.buttonBg || '#3b82f6';
  const buttonTextColor =
    data.customColors?.buttonText || themeStyles.imageCard?.buttonText || '#ffffff';

  // Determine if background is a gradient or solid color
  const isGradient = backgroundColor.includes('gradient');
  const backgroundStyle: React.CSSProperties = {};
  if (isGradient) {
    backgroundStyle.background = backgroundColor;
  } else {
    backgroundStyle.backgroundColor = backgroundColor;
  }

  // Sort items by order
  const sortedItems = [...(data.items || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  // Get image URL
  const getImageUrl = (item: (typeof sortedItems)[0]) => {
    return item.cloudfrontUrl || item.imageUrl || '';
  };

  // Grid column classes
  const getGridColsClass = () => {
    switch (data.columns) {
      case 1:
        return 'grid-cols-1';
      case 2:
        return 'grid-cols-1 md:grid-cols-2';
      case 3:
        return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
      case 4:
        return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4';
      default:
        return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
    }
  };

  // Aspect ratio classes
  const getAspectRatioClass = () => {
    switch (data.aspectRatio) {
      case 'square':
        return 'aspect-square';
      case 'portrait':
        return 'aspect-[3/4]';
      case 'landscape':
        return 'aspect-video';
      default:
        return 'aspect-square';
    }
  };

  // Spacing classes
  const getSpacingClass = () => {
    switch (data.spacing) {
      case 'none':
        return 'gap-0';
      case 'small':
        return 'gap-2 md:gap-3';
      case 'normal':
        return 'gap-4 md:gap-6';
      case 'large':
        return 'gap-6 md:gap-8';
      default:
        return 'gap-4 md:gap-6';
    }
  };

  // Text position classes
  const getTextPositionClass = () => {
    switch (data.textPosition) {
      case 'top':
        return 'items-start pt-6';
      case 'center':
        return 'items-center';
      case 'bottom':
        return 'items-end pb-6';
      default:
        return 'items-center';
    }
  };

  // Get button styles based on button type (for section-level CTA buttons)
  const getButtonStyles = (style: string) => {
    switch (style) {
      case 'primary':
        return {
          backgroundColor: buttonBgColor,
          color: buttonTextColor,
          border: 'none',
        };
      case 'secondary':
        return {
          backgroundColor: 'rgba(128, 128, 128, 0.1)',
          color: buttonBgColor,
          border: `2px solid ${buttonBgColor}`,
        };
      case 'outline':
        return {
          backgroundColor: 'transparent',
          color: buttonBgColor,
          border: `2px solid ${buttonBgColor}`,
        };
      default:
        return {
          backgroundColor: buttonBgColor,
          color: buttonTextColor,
          border: 'none',
        };
    }
  };

  // Render section-level CTA buttons
  const renderButtons = () => {
    const buttons = (data as any).buttons;
    if (!buttons || buttons.length === 0) return null;

    return (
      <div className="flex flex-wrap justify-center gap-4 mt-12">
        {buttons.map((button: any, index: number) => (
          <ButtonLink
            key={index}
            url={button.url}
            target={button.target}
            className="px-6 py-3 font-semibold rounded-lg transition-all duration-200 hover:opacity-90 hover:shadow-md inline-block"
            style={getButtonStyles(button.style)}
          >
            {button.text}
          </ButtonLink>
        ))}
      </div>
    );
  };

  // Get overlay styles
  const getOverlayStyles = (): React.CSSProperties => {
    const opacity = (data.overlayOpacity ?? 50) / 100;

    switch (data.overlayStyle) {
      case 'none':
        return {};
      case 'light':
        return {
          backgroundColor: `rgba(255, 255, 255, ${opacity})`,
        };
      case 'dark':
        return {
          backgroundColor: `rgba(0, 0, 0, ${opacity})`,
        };
      case 'gradient':
        return {
          background: `linear-gradient(to top, ${overlayColor}cc 0%, ${overlayColor}66 50%, transparent 100%)`,
        };
      default:
        return {
          backgroundColor: `rgba(0, 0, 0, ${opacity})`,
        };
    }
  };

  // Render a single card
  const renderCard = (item: (typeof sortedItems)[0]) => {
    const imageUrl = getImageUrl(item);
    const hasLink = !!item.url;
    const hasEffects = hasCropApplied((item as any).crop);
    const cropStyles = getCroppedImageStyles((item as any).crop);

    const cardContent = (
      <div className={`relative overflow-hidden rounded-lg ${getAspectRatioClass()} group`}>
        {/* Background Image */}
        {imageUrl && (
          <div className="absolute inset-0 w-full h-full overflow-hidden transition-transform duration-300 group-hover:scale-105">
            <img
              src={imageUrl}
              alt={item.alt || item.title}
              className={!hasEffects ? 'w-full h-full object-cover' : ''}
              style={hasEffects ? cropStyles : undefined}
            />
          </div>
        )}

        {/* Placeholder if no image */}
        {!imageUrl && (
          <div className="absolute inset-0 w-full h-full bg-gray-300 flex items-center justify-center">
            <span className="text-gray-500 text-sm">No image</span>
          </div>
        )}

        {/* Overlay */}
        {data.overlayStyle !== 'none' && (
          <div
            className="absolute inset-0 w-full h-full transition-opacity duration-300"
            style={getOverlayStyles()}
          />
        )}

        {/* Content */}
        <div
          className={`absolute inset-0 flex flex-col justify-center px-6 ${getTextPositionClass()}`}
        >
          {/* Title */}
          <h3
            className="text-xl md:text-2xl font-bold text-center drop-shadow-lg"
            style={{ color: titleColor, ...headingFontStyle }}
          >
            {item.title}
          </h3>

          {/* Description */}
          {item.description && (
            <p
              className="mt-3 text-sm md:text-base text-center max-w-md mx-auto drop-shadow-md"
              style={{ color: descriptionColor, ...bodyFontStyle }}
            >
              {item.description}
            </p>
          )}

          {/* Button */}
          {item.button && (
            <div className="mt-4 flex justify-center">
              <span
                className={`inline-block px-6 py-2 rounded-lg font-medium text-sm transition-all duration-200 hover:scale-105 ${
                  item.button.style === 'outline'
                    ? 'bg-transparent border-2'
                    : item.button.style === 'ghost'
                      ? 'bg-transparent'
                      : item.button.style === 'secondary'
                        ? 'bg-white/20 backdrop-blur-sm'
                        : ''
                }`}
                style={{
                  backgroundColor: item.button.style === 'primary' ? buttonBgColor : undefined,
                  color: item.button.style === 'primary' ? buttonTextColor : buttonBgColor,
                  borderColor: item.button.style === 'outline' ? buttonBgColor : undefined,
                }}
              >
                {item.button.text}
              </span>
            </div>
          )}
        </div>

        {/* Hover effect indicator for linked cards */}
        {hasLink && (
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
        )}
      </div>
    );

    // Wrap in link if URL provided
    if (hasLink) {
      return (
        <ButtonLink
          key={item.id}
          url={item.url || '/'}
          target={item.target}
          className="block cursor-pointer"
        >
          {cardContent}
        </ButtonLink>
      );
    }

    return <div key={item.id}>{cardContent}</div>;
  };

  return (
    <section className="py-12 md:py-16 lg:py-24" style={backgroundStyle}>
      <div className="container mx-auto min-w-0 px-4 md:px-6 lg:px-8">
        {/* Section Header */}
        {(data.showTitle || data.showSubtitle) && (
          <div className="text-center mb-8 md:mb-12">
            {data.showTitle && data.title && (
              <h2
                className="text-3xl md:text-4xl font-bold mb-4"
                style={{
                  color: titleColor === '#ffffff' ? '#111827' : titleColor,
                  ...headingFontStyle,
                }}
              >
                {data.title}
              </h2>
            )}
            {data.showSubtitle && data.subtitle && (
              <p
                className="text-lg md:text-xl"
                style={{
                  color: descriptionColor === '#e5e7eb' ? '#6b7280' : descriptionColor,
                  ...bodyFontStyle,
                }}
              >
                {data.subtitle}
              </p>
            )}
          </div>
        )}

        {/* Cards Grid */}
        <div className={`grid ${getGridColsClass()} ${getSpacingClass()} [&>*]:min-w-0`}>
          {sortedItems.map(item => renderCard(item))}
        </div>

        {/* Call-to-Action Buttons */}
        {renderButtons()}
      </div>
    </section>
  );
};
